import { Disclosure, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { BsArrowRight } from "react-icons/bs";
import { CiDeliveryTruck, CiGift, CiCircleCheck, CiBoxes } from "react-icons/ci";

const statusConfig = {
  pending: {
    label: "Awaiting Pickup",
    badgeCls: "bg-amber-50 text-amber-700 border-amber-200",
    accentCls: "border-l-amber-400",
  },
  collected: {
    label: "Collected",
    badgeCls: "bg-sky-50 text-sky-700 border-sky-200",
    accentCls: "border-l-sky-500",
  },
  enroute: {
    label: "En Route",
    badgeCls: "bg-violet-50 text-violet-700 border-violet-200",
    accentCls: "border-l-violet-600",
  },
  ready_for_collection: {
    label: "Ready for Collection",
    badgeCls: "bg-teal-50 text-teal-700 border-teal-200",
    accentCls: "border-l-teal-500",
  },
  delivered: {
    label: "Delivered",
    badgeCls: "bg-emerald-50 text-emerald-700 border-emerald-200",
    accentCls: "border-l-emerald-500",
  },
};

function DetailItem({ label, value, children }) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">
        {label}
      </p>
      {children ?? <p className="text-sm text-gray-700 mt-0.5">{value}</p>}
    </div>
  );
}

export default function OrderCard({ order }) {
  const isLimpopo = order.destination_province?.name === "Limpopo";

  const limpopoStatuses = { pending: 0, collected: 1, enroute: 2, ready_for_collection: 3, delivered: 4 };
  const gautengStatuses = { pending: 0, collected: 1, enroute: 2, delivered: 3 };

  const orderStatuses = isLimpopo ? limpopoStatuses : gautengStatuses;
  const deliveryStatus = order.delivery_status ?? "pending";
  const step = orderStatuses[deliveryStatus] ?? 0;
  const config = statusConfig[deliveryStatus] ?? statusConfig.pending;

  const options = { day: "numeric", month: "short", year: "numeric" };
  const formattedDate = new Date(order.collection_date).toLocaleDateString(
    "en-US",
    options,
  );

  const needsPayment = order.status === "created";
  const payLink = `https://www.payfast.co.za/eng/process?cmd=_paynow&receiver=11799876&item_name=Parcelin%20Order+${order?.reference_number}&item_description=Parcelin%20Order+R${order?.price?.value * order.quantity}+deposit+per+seat&amount=${order?.price?.value * order.quantity}&return_url=https://parcelin.co.za/payment-success&cancel_url=https://parcelin.co.za/cancel_payment?parcel_order_id=${order?.id}`;

  const StepDot = ({ icon: Icon, label, dotStep }) => {
    const completed = step > dotStep;
    const current = step === dotStep;
    const isTruck = label === "En Route";
    return (
      <div className="flex flex-col items-center flex-shrink-0">
        <div className="relative">
          {current && (
            <span className="absolute inset-0 rounded-full bg-violet-400 opacity-40 animate-ping" />
          )}
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
              completed
                ? "bg-violet-600 border-violet-600"
                : current
                  ? "bg-violet-100 border-violet-500"
                  : "bg-white border-gray-200"
            }`}
          >
            <Icon
              size={15}
              color={completed ? "white" : current ? "#7c3aed" : "#d1d5db"}
              className={current && isTruck ? "animate-bounce" : ""}
            />
          </div>
        </div>
        <span
          className={`text-xs mt-1 font-medium transition-colors duration-300 ${
            completed || current ? "text-violet-700" : "text-gray-400"
          }`}
        >
          {label}
        </span>
      </div>
    );
  };

  const Line = ({ active }) => (
    <div
      className={`flex-1 h-0.5 mb-5 transition-colors duration-500 ${active ? "bg-violet-500" : "bg-gray-200"}`}
    />
  );

  return (
    <Disclosure>
      {({ open }) => (
        <div
          className={`bg-white rounded-xl shadow-sm border border-gray-100 border-l-4 ${config.accentCls} overflow-hidden`}
        >
          <Disclosure.Button className="w-full text-left px-5 pt-4 pb-3">
            {/* Route + meta + badge */}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[15px] font-bold text-gray-900">
                    {order.departure_province?.name || '—'}
                  </span>
                  <BsArrowRight
                    className="text-gray-400 flex-shrink-0"
                    size={13}
                  />
                  <span className="text-[15px] font-bold text-gray-900">
                    {order.destination_province?.name || '—'}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  {formattedDate} ·{" "}
                  <span className="font-medium text-gray-500">
                    #{order.reference_number}
                  </span>
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 pt-0.5">
                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${config.badgeCls}`}
                >
                  {config.label}
                </span>
                <ChevronDownIcon
                  className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                />
              </div>
            </div>

            {/* Progress stepper */}
            <div className="flex items-center mt-4">
              <StepDot icon={CiGift} label="Collected" dotStep={1} />
              <Line active={step >= 2} />
              <StepDot icon={CiDeliveryTruck} label="En Route" dotStep={2} />
              {isLimpopo ? (
                <>
                  <Line active={step >= 3} />
                  <StepDot icon={CiBoxes} label="Ready" dotStep={3} />
                  <Line active={step >= 4} />
                  <StepDot icon={CiCircleCheck} label="Delivered" dotStep={4} />
                </>
              ) : (
                <>
                  <Line active={step >= 3} />
                  <StepDot icon={CiCircleCheck} label="Delivered" dotStep={3} />
                </>
              )}
            </div>
          </Disclosure.Button>

          <Transition
            show={open}
            enter="transition duration-200 ease-out"
            enterFrom="opacity-0 -translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition duration-150 ease-in"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-1"
          >
            <Disclosure.Panel
              static
              className="border-t border-gray-100 bg-gray-50/60 px-5 py-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
                <DetailItem
                  label="Delivery Type"
                  value={`${order.price?.price_type?.name || 'Standard'} · Collection`}
                />
                <DetailItem
                  label="Collection Address"
                  value={order.pickup_location}
                />
                <DetailItem label="Payment">
                  <div className="flex items-center gap-2 mt-0.5">
                    {/* <span className="text-sm font-semibold text-gray-800">R{order.price.value * order.quantity}</span> */}
                    {needsPayment ? (
                      <a
                        href={payLink}
                        className="px-2.5 py-0.5 text-xs font-semibold bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
                      >
                        Pay Now
                      </a>
                    ) : (
                      <span className="px-2.5 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                        {order.status.replace(/_/g, " ").toUpperCase()}
                      </span>
                    )}
                  </div>
                </DetailItem>
                <DetailItem label="Collection Date" value={formattedDate} />
                <DetailItem
                  label="Delivery Address"
                  value={order?.drop_off_location || order?.destination_town?.name || "-"}
                />
                <DetailItem label="Tracking Number">
                  <p className="text-sm font-mono text-gray-700 mt-0.5 tracking-wide">
                    {order.reference_number}
                  </p>
                </DetailItem>
                <DetailItem label="Waybill">
                  <a
                    href={`${process.env.REACT_APP_API_URL}parcel_orders/${order.id}/waybill`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-semibold text-violet-600 hover:text-violet-800 mt-0.5"
                  >
                    Download PDF
                  </a>
                </DetailItem>
              </div>
            </Disclosure.Panel>
          </Transition>
        </div>
      )}
    </Disclosure>
  );
}
